<template>
  <v-container grid-list-md>
    <v-layout row justify-center>
      <v-flex xs12 md8 lg6>
        <!-- Loading Skeleton -->
        <v-card v-if="isLoading" class="mx-auto mb-4">
          <v-card-title>
            <v-progress-circular
              indeterminate
              color="primary"
              class="mr-3"
            ></v-progress-circular>
            Carregando dados do pagamento...
          </v-card-title>
          <v-card-text>
            <v-progress-linear
              indeterminate
              color="primary"
              class="mb-3"
            ></v-progress-linear>
            <div class="text-xs-center">
              <p class="subheading">Aguarde enquanto buscamos as informações...</p>
            </div>
          </v-card-text>
        </v-card>

        <!-- Content -->
        <v-card v-else id="div_print" class="mx-auto mb-4" elevation="3">
          <!-- Header -->
          <v-card-title class="primary white--text d-flex align-center">
            <v-icon left color="white" size="28">view_stream</v-icon>
            <span class="headline font-weight-medium">
              Pagamento #{{ bill.id ? String(bill.id).padStart(6, '0') : '------' }}
            </span>
            <v-spacer></v-spacer>
            <!-- Print button in header -->
            <v-btn 
              icon 
              color="white" 
              @click="printDiv"
              class="noprint"
              title="Imprimir comprovante"
            >
              <v-icon>print</v-icon>
            </v-btn>
          </v-card-title>

          <!-- Status Badge -->
          <div class="px-6 pt-4 pb-2">
            <v-chip 
              :class="setClass(bill.status)" 
              large 
              label
              text-color="white"
              class="font-weight-bold"
            >
              <v-icon left small>{{ getStatusIcon(bill.status) }}</v-icon>
              {{ $t(`status.${bill.status}`) }}
            </v-chip>
          </div>

          <!-- Payment Details -->
          <v-card-text class="pa-4">
            <v-layout row wrap>
              <!-- Valor -->
              <v-flex xs12 sm6>
                <div class="detail-item">
                  <div class="detail-label">
                    <v-icon small color="primary" class="mr-2">attach_money</v-icon>
                    Valor do Pagamento
                  </div>
                  <div class="detail-value headline primary--text font-weight-bold">
                    {{ $n((bill.bill_amount), 'currency') }}
                  </div>
                </div>
              </v-flex>

              <!-- Data de Criação -->
              <v-flex xs12 sm6>
                <div class="detail-item">
                  <div class="detail-label">
                    <v-icon small color="grey darken-1" class="mr-2">schedule</v-icon>
                    Data de Criação
                  </div>
                  <div class="detail-value">
                    {{ setDateFormat(bill.created_at) }} às {{ setTimeFormat(bill.created_at) }}
                  </div>
                </div>
              </v-flex>

              <!-- Nosso Número -->
              <v-flex xs12 sm6 v-if="bill.our_number">
                <div class="detail-item">
                  <div class="detail-label">
                    <v-icon small color="grey darken-1" class="mr-2">confirmation_number</v-icon>
                    Nosso Número
                  </div>
                  <div class="detail-value font-family-monospace">
                    {{ bill.our_number }}
                  </div>
                </div>
              </v-flex>

              <!-- Protocolo -->
              <v-flex xs12 sm6 v-if="bill.protocol">
                <div class="detail-item">
                  <div class="detail-label">
                    <v-icon small color="grey darken-1" class="mr-2">receipt</v-icon>
                    Protocolo
                  </div>
                  <div class="detail-value font-family-monospace">
                    {{ bill.protocol }}
                  </div>
                </div>
              </v-flex>

              <!-- Data do Pagamento -->
              <v-flex xs12 sm6 v-if="bill.payment_date">
                <div class="detail-item">
                  <div class="detail-label">
                    <v-icon small color="green" class="mr-2">event_available</v-icon>
                    Data do Pagamento
                  </div>
                  <div class="detail-value primary--text">
                    {{ setDateFormat(bill.payment_date) }}
                  </div>
                </div>
              </v-flex>

              <!-- Código de Barras -->
              <v-flex xs12 v-if="bill.digitable_line">
                <div class="detail-item">
                  <div class="detail-label">
                    <v-icon small color="grey darken-1" class="mr-2">view_stream</v-icon>
                    Código de Barras / Linha Digitável
                  </div>
                  <div class="detail-value">
                    <v-card class="pa-3 mt-2" color="grey lighten-5">
                      <div class="barcode-container text-xs-center">
                        <div class="barcode-visual mb-2">
                          <div class="barcode-lines">
                            <span v-for="n in 30" :key="n" class="barcode-line" :style="{height: Math.random() * 20 + 10 + 'px'}"></span>
                          </div>
                        </div>
                        <div class="barcode-text font-family-monospace body-2">
                          {{ formatDigitableLine(bill.digitable_line) }}
                        </div>
                      </div>
                    </v-card>
                  </div>
                </div>
              </v-flex>
            </v-layout>

            <!-- Messages Section -->
            <v-divider class="my-4"></v-divider>
            
            <!-- Return Message -->
            <div v-if="bill.return_message" class="detail-item mb-3">
              <div class="detail-label">
                <v-icon small color="info" class="mr-2">info</v-icon>
                Mensagem de Retorno
              </div>
              <v-alert
                type="info"
                outlined
                dense
                class="mt-2 mb-0"
              >
                {{ bill.return_message }}
              </v-alert>
            </div>

            <!-- Error Message -->
            <div v-if="bill.error_message" class="detail-item">
              <div class="detail-label">
                <v-icon small color="error" class="mr-2">error</v-icon>
                Mensagem de Erro
              </div>
              <v-alert
                type="error"
                outlined
                dense
                class="mt-2 mb-0"
              >
                {{ bill.error_message }}
              </v-alert>
            </div>
          </v-card-text>
        </v-card>

        <!-- Action Buttons -->
        <v-card v-if="!isLoading" class="noprint">
          <v-card-actions class="pa-4">
            <v-layout row wrap align-center>
              <v-flex shrink>
                <v-btn
                  color="primary"
                  @click.stop="goTo('bill-payments')"
                  class="mr-2"
                >
                  <v-icon left>arrow_back</v-icon>
                  Voltar
                </v-btn>
              </v-flex>
              
              <v-spacer></v-spacer>
              
              <v-flex shrink>
                <!-- Print Button - destacado -->
                <v-btn 
                  color="success" 
                  @click="printDiv"
                  class="mr-2"
                >
                  <v-icon left>print</v-icon>
                  <span class="hidden-xs-only">Imprimir Comprovante</span>
                  <span class="hidden-sm-and-up">Imprimir</span>
                </v-btn>
                
                <!-- Download/Share options -->
                <v-menu offset-y left>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      color="grey"
                      outline
                      v-on="on"
                      small
                      icon
                    >
                      <v-icon>more_vert</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-tile @click="printDiv">
                      <v-list-tile-action>
                        <v-icon small>print</v-icon>
                      </v-list-tile-action>
                      <v-list-tile-content>
                        <v-list-tile-title>Imprimir</v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-list-tile @click="downloadPDF">
                      <v-list-tile-action>
                        <v-icon small>picture_as_pdf</v-icon>
                      </v-list-tile-action>
                      <v-list-tile-content>
                        <v-list-tile-title>Salvar PDF</v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </v-flex>
            </v-layout>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'ShowBillPayments',
  data () {
    return {

    }
  },
  computed: {
    ...mapGetters({
      bill: 'bills/showBillPayments',
      isLoading: 'bills/loadingData'
    }),
  },
  created() {
    this.$store.dispatch('bills/showBillPayments', parseInt(this.$route.params.id));
  },
  components: {

  },
  methods: {
    setAmount(amount){
      return (amount / 100)
    },
    setDateFormat(date) {
      if (date === null) {
        return ''
      } else {
        return new Date(date).toLocaleDateString("pt-BR")
      }
    },
    setTimeFormat(date) {
      if (date === null) {
        return ''
      } else {
        return new Date(date).toLocaleTimeString("pt-BR")
      }
    },
    goTo(route) {
      this.$router.push({ name: route });
    },
    getStatusIcon(status) {
      const icons = {
        'paid': 'check_circle',
        'waiting_payment_day': 'schedule',
        'rejected': 'cancel',
        'pending': 'hourglass_empty',
        'processing': 'sync'
      };
      return icons[status] || 'help';
    },
    formatDigitableLine(line) {
      if (!line) return '';
      
      // Remove todos os caracteres não numéricos
      const cleanLine = line.replace(/[^0-9]/g, '');
      
      // Se tem 47 dígitos, é linha digitável
      if (cleanLine.length === 47) {
        return cleanLine.replace(/(\d{5})(\d{5})(\d{5})(\d{6})(\d{5})(\d{6})(\d{1})(\d{14})/, 
          '$1.$2 $3.$4 $5.$6 $7 $8');
      }
      
      // Se tem 44 dígitos, é código de barras (mostrar sem formatação para leitura)
      if (cleanLine.length === 44) {
        return cleanLine.replace(/(\d{4})(\d{5})(\d{5})(\d{6})(\d{5})(\d{6})(\d{1})(\d{11})/, 
          '$1 $2 $3 $4 $5 $6 $7 $8');
      }
      
      // Retornar como está se não for nenhum dos formatos conhecidos
      return line;
    },
    setClass(status) {
      return `invoice ${status}`;
    },
    printDiv () {
      // Get HTML to print from element
      const prtHtml = document.getElementById('div_print').innerHTML;

      // Get all stylesheets HTML
      let stylesHtml = '';
      for (const node of [...document.querySelectorAll('link[rel="stylesheet"], style')]) {
        stylesHtml += node.outerHTML;
      }

      // Custom print styles
      const printStyles = `
        <style>
          @media print {
            body { 
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #000 !important;
            }
            .v-card {
              border: 1px solid #ccc !important;
              box-shadow: none !important;
              margin-bottom: 20px;
            }
            .barcode-line {
              background-color: #000 !important;
            }
            .primary--text {
              color: #1976d2 !important;
            }
            .success--text {
              color: #4caf50 !important;
            }
            .error--text {
              color: #f44336 !important;
            }
            .headline {
              font-size: 1.5rem !important;
              font-weight: bold !important;
            }
          }
        </style>
      `;

      // Open the print window
      const WinPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0');

      WinPrint.document.write(`<!DOCTYPE html>
      <html>
        <head>
          <title>Comprovante de Pagamento #${this.bill.id || '---'}</title>
          ${stylesHtml}
          ${printStyles}
        </head>
        <body>
          ${prtHtml}
        </body>
      </html>`);

      WinPrint.document.close();
      WinPrint.focus();
      WinPrint.print();
      WinPrint.close();
    },
    downloadPDF() {
      // Implementação básica - usar window.print() com configuração de PDF
      // O usuário pode escolher "Salvar como PDF" no diálogo de impressão
      this.printDiv();
      
      // Mostrar dica ao usuário
      this.$nextTick(() => {
        // Pode implementar um toast/snackbar aqui se disponível
        console.log('Dica: No diálogo de impressão, selecione "Salvar como PDF" como destino');
      });
    }
  },
  watch: {

  },
};
</script>
<style scoped>
  .detail-item {
    margin-bottom: 16px;
  }

  .detail-label {
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.6);
    font-weight: 500;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
  }

  .detail-value {
    font-size: 1rem;
    color: rgba(0, 0, 0, 0.87);
    font-weight: 400;
    word-break: break-all;
  }

  .font-family-monospace {
    font-family: 'Roboto Mono', monospace;
  }

  /* Bill Payment Status Colors */
  .v-chip.invoice.pending { 
    background-color: #1976D2 !important; 
  }
  .v-chip.invoice.check_payment { 
    background-color: #29B6F6 !important; 
  }
  .v-chip.invoice.waiting_payment_day { 
    background-color: #FFC107 !important; 
  }
  .v-chip.invoice.paid { 
    background-color: #4CAF50 !important; 
  }
  .v-chip.invoice.rejected { 
    background-color: #F44336 !important; 
  }
  .v-chip.invoice.unknow { 
    background-color: #9E9E9E !important; 
  }
  .v-chip.invoice.processing { 
    background-color: #1976D2 !important; 
  }

  /* Print styles */
  @media print {
    .noprint {
      display: none;
    }
    
    .v-container {
      padding: 0 !important;
    }
    
    .v-card {
      box-shadow: none !important;
      border: 1px solid #ddd;
    }
  }

  /* Card hover effect */
  .v-card {
    transition: all 0.3s ease;
  }

  .v-card:hover {
    transform: translateY(-2px);
  }

  /* Barcode visual styles */
  .barcode-container {
    max-width: 100%;
  }

  .barcode-visual {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    height: 40px;
    overflow: hidden;
  }

  .barcode-lines {
    display: flex;
    align-items: flex-end;
    gap: 1px;
    height: 100%;
  }

  .barcode-line {
    background-color: #000;
    width: 2px;
    display: inline-block;
    transition: height 0.3s ease;
  }

  .barcode-line:nth-child(even) {
    width: 1px;
  }

  .barcode-line:nth-child(3n) {
    width: 3px;
  }

  .barcode-text {
    color: #333;
    font-size: 0.9rem;
    letter-spacing: 1px;
    word-break: break-all;
  }

  /* Print optimization for barcode */
  @media print {
    .barcode-visual {
      height: 30px;
    }
    
    .barcode-line {
      background-color: #000 !important;
    }
  }
</style>
